/**
 * eprSubmissionService.ts
 *
 * Firestore CRUD + state transitions for EPRSubmission (hr_v1 path).
 *
 * State machine (CLAUDE.md §C, §F):
 *   draft → employee_submitted → manager_submitted → locked
 *
 * Lock is one-way. Post-lock changes happen only through calibration
 * adjustments (separate collection, per §E.6). There is no `unlock`.
 *
 * Item-level edits use field-path updates so auto-save can patch one field
 * without rewriting the whole document.
 */

import {
  collection, doc, getDoc, setDoc, getDocs,
  query, where, serverTimestamp, runTransaction, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  EPRSubmission, EPRSubmissionHeader, EPRItem, EPRWeights,
  EPRSubmissionStatus, EPRTemplateSnapshot,
} from '../types/eprFramework';
import {
  computeSubmissionScores, validateSubmissionForSubmit,
  computeItemFinalScore, type ValidationIssue,
} from './eprScoreEngine';
import { omitUndefinedDeep } from './firestoreUtils';

const COL = 'eprSubmissions';

// ─── ID helper ──────────────────────────────────────────────────────────────

/**
 * Composite key per CLAUDE.md §C1.4 — idempotent, easy to look up by
 * (cycleId, employeeId).
 */
export function submissionId(cycleId: string, employeeId: string): string {
  return `${cycleId}_${employeeId}`;
}

// ─── Errors ─────────────────────────────────────────────────────────────────

export class EPRSubmissionError extends Error {
  constructor(
    public code:
      | 'not_found'
      | 'wrong_state'
      | 'validation_failed'
      | 'unauthorized',
    message: string,
    public issues?: ValidationIssue[],
  ) {
    super(message);
    this.name = 'EPRSubmissionError';
  }
}

// ─── Create ─────────────────────────────────────────────────────────────────

export interface CreateSubmissionInput {
  cycleId: string;
  employeeId: string;
  supervisorId: string;
  templateId: string;
  weights: EPRWeights;
  okrItems: EPRItem[];
  kpiItems: EPRItem[];
  coreItems: EPRItem[];
  leadershipItems: EPRItem[];     // pass [] when weights.leadership === 0
  kpiCatalogId?: string;
  /** Canonical form header snapshot (names, position, dept, period). */
  header?: EPRSubmissionHeader;
  /** Frozen template copy (scale, sources, competencies + rubrics). */
  templateSnapshot?: EPRTemplateSnapshot;
}

/**
 * Idempotent create — if the composite ID already exists, leave the doc
 * untouched (do not clobber in-progress fills). Returns the existing or
 * newly-created submission.
 */
export async function createSubmission(input: CreateSubmissionInput): Promise<EPRSubmission> {
  const id = submissionId(input.cycleId, input.employeeId);
  const ref = doc(db, COL, id);

  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { id: snap.id, ...(snap.data() as Omit<EPRSubmission, 'id'>) };
  }

  const data: Omit<EPRSubmission, 'id'> = {
    cycleId:        input.cycleId,
    employeeId:     input.employeeId,
    supervisorId:   input.supervisorId,
    templateId:     input.templateId,
    weights:        input.weights,
    status:         'draft' as EPRSubmissionStatus,
    okrItems:       input.okrItems,
    kpiItems:       input.kpiItems,
    coreItems:      input.coreItems,
    leadershipItems: input.leadershipItems,
    ...(input.header
      ? { header: omitUndefinedDeep({ ...input.header } as Record<string, unknown>) as unknown as EPRSubmissionHeader }
      : {}),
    ...(input.templateSnapshot
      ? { templateSnapshot: omitUndefinedDeep({ ...input.templateSnapshot } as Record<string, unknown>) as unknown as EPRTemplateSnapshot }
      : {}),
    ...(input.kpiCatalogId ? { kpiCatalogId: input.kpiCatalogId } : {}),
    scores:         {},
    createdAt:      serverTimestamp() as unknown as Timestamp,
  };
  await setDoc(ref, data);
  const created = await getDoc(ref);
  return { id: created.id, ...(created.data() as Omit<EPRSubmission, 'id'>) };
}

// ─── Read ───────────────────────────────────────────────────────────────────

export async function getSubmission(id: string): Promise<EPRSubmission | null> {
  const snap = await getDoc(doc(db, COL, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<EPRSubmission, 'id'>) };
}

export async function getSubmissionByEmployee(
  cycleId: string,
  employeeId: string,
): Promise<EPRSubmission | null> {
  return getSubmission(submissionId(cycleId, employeeId));
}

export async function listSubmissionsForCycle(cycleId: string): Promise<EPRSubmission[]> {
  const q = query(collection(db, COL), where('cycleId', '==', cycleId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<EPRSubmission, 'id'>) }));
}

export async function listSubmissionsForManager(
  cycleId: string,
  managerId: string,
): Promise<EPRSubmission[]> {
  const q = query(
    collection(db, COL),
    where('cycleId', '==', cycleId),
    where('supervisorId', '==', managerId),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<EPRSubmission, 'id'>) }));
}

// ─── Item-level edits (auto-save) ───────────────────────────────────────────

type ItemSide = 'employeeSelfScore' | 'managerScore';

type ItemBucket = 'okrItems' | 'kpiItems' | 'coreItems' | 'leadershipItems';

interface ItemPatch {
  /** Integer within the submission's scale snapshot (default 1–5 per §C). */
  score?: number;
  notesEvidence?: string;
  target?: string;
  actual?: string;
  achievementPct?: number;
  /** Editable only on employee-added items (manual KPI rows). */
  itemName?: string;
}

/**
 * Patch a single item by composing the new items array and writing it back.
 * Uses a transaction so concurrent auto-saves on different items don't
 * race-overwrite each other.
 *
 * Disallowed when submission is in 'locked' state. Also disallowed for the
 * non-scoring side once the scoring side has submitted (e.g. employee
 * cannot edit after employee_submitted; manager cannot edit after
 * manager_submitted).
 */
export async function patchItem(
  submissionId: string,
  itemId: string,
  side: ItemSide,
  patch: ItemPatch,
): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, submissionId);
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new EPRSubmissionError('not_found', 'Submission not found.');
    }
    const sub = snap.data() as EPRSubmission;

    if (sub.status === 'locked') {
      throw new EPRSubmissionError('wrong_state', 'Submission is locked.');
    }
    if (side === 'employeeSelfScore' && sub.status !== 'draft') {
      throw new EPRSubmissionError(
        'wrong_state',
        'Employee can only edit while submission is in draft.',
      );
    }
    if (side === 'managerScore'
        && sub.status !== 'draft'
        && sub.status !== 'employee_submitted') {
      throw new EPRSubmissionError(
        'wrong_state',
        'Manager can only edit before manager submit.',
      );
    }

    const findBucket = locateItem(sub, itemId);
    if (!findBucket) {
      throw new EPRSubmissionError('not_found', `Item ${itemId} not found in submission.`);
    }
    const { bucket, index, item } = findBucket;

    const updated: EPRItem = { ...item };
    if (typeof patch.score === 'number') updated[side] = patch.score;
    if (typeof patch.notesEvidence === 'string') updated.notesEvidence = patch.notesEvidence;
    if (typeof patch.target === 'string')        updated.target = patch.target;
    if (typeof patch.actual === 'string')        updated.actual = patch.actual;
    if (typeof patch.achievementPct === 'number') updated.achievementPct = patch.achievementPct;
    if (typeof patch.itemName === 'string' && item.addedBy === 'employee' && patch.itemName.trim()) {
      updated.itemName = patch.itemName;
    }
    const final = computeItemFinalScore(updated);
    if (final !== undefined) updated.itemFinalScore = final;

    const newArray = [...sub[bucket]];
    newArray[index] = updated;

    // Scrub undefined leaves out of every item in the bucket before the
    // write. Items legitimately have optional fields (notesEvidence,
    // target, actual, achievementPct, scores, itemFinalScore) — leaving
    // those as `undefined` causes Firestore to reject the whole
    // transaction with: "Function Transaction.update() called with
    // invalid data. Unsupported field value: undefined".
    //
    // ENGINEERING.md §4: setDoc/addDoc/batch.set/tx.update with raw
    // input that has optional fields → always omitUndefinedDeep first.
    const payload = omitUndefinedDeep({
      [bucket]: newArray,
      updatedAt: serverTimestamp(),
    });
    tx.update(ref, payload);
  });
}

// ─── Employee-created items (OKR pick / manual KPI rows) ────────────────────
//
// In the low-friction flow HR seeds NO okr/kpi items at launch. The employee
// builds them in the magic-link form: OKR items by selecting synced KRs from
// the OKR Tracker, KPI items by typing rows per the canonical EPR Form.
// Both paths land here. Core/Leadership items stay template-driven and are
// never addable or removable.

export type EmployeeItemComponent = 'okr' | 'kpi';

export interface AddEmployeeItemInput {
  component: EmployeeItemComponent;
  /** Stable id — the KR id for OKR picks; generated for manual KPI rows. */
  itemId: string;
  itemName: string;
  description?: string;
  target?: string;
}

/**
 * Append an employee-created item. Draft-only (the employee is the only
 * side allowed to define their items, and only before self-submit).
 * Idempotent on itemId — re-picking an already-added KR is a no-op, so a
 * double-click or re-sync can't duplicate rows.
 */
export async function addEmployeeItem(
  submissionId: string,
  input: AddEmployeeItemInput,
): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, submissionId);
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new EPRSubmissionError('not_found', 'Submission not found.');
    }
    const sub = snap.data() as EPRSubmission;

    if (sub.status !== 'draft') {
      throw new EPRSubmissionError(
        'wrong_state',
        'Items can only be added while the submission is in draft.',
      );
    }
    if (!input.itemName.trim()) {
      throw new EPRSubmissionError('validation_failed', 'Item name is required.');
    }

    const bucket: ItemBucket = input.component === 'okr' ? 'okrItems' : 'kpiItems';
    if (sub[bucket].some(it => it.itemId === input.itemId)) return;

    const item = omitUndefinedDeep({
      itemId:      input.itemId,
      component:   input.component,
      itemName:    input.itemName.trim(),
      description: input.description,
      target:      input.target,
      addedBy:     'employee',
    }) as unknown as EPRItem;

    tx.update(ref, {
      [bucket]: [...sub[bucket], item],
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Remove an employee-created okr/kpi item. Draft-only, and refuses to
 * touch launch-seeded or template items — the employee can only undo
 * their own additions.
 */
export async function removeEmployeeItem(
  submissionId: string,
  itemId: string,
): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, submissionId);
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new EPRSubmissionError('not_found', 'Submission not found.');
    }
    const sub = snap.data() as EPRSubmission;

    if (sub.status !== 'draft') {
      throw new EPRSubmissionError(
        'wrong_state',
        'Items can only be removed while the submission is in draft.',
      );
    }

    const found = locateItem(sub, itemId);
    if (!found) {
      throw new EPRSubmissionError('not_found', `Item ${itemId} not found in submission.`);
    }
    if (found.bucket !== 'okrItems' && found.bucket !== 'kpiItems') {
      throw new EPRSubmissionError('wrong_state', 'Core/Leadership items cannot be removed.');
    }
    if (found.item.addedBy !== 'employee') {
      throw new EPRSubmissionError('wrong_state', 'Only employee-added items can be removed.');
    }

    tx.update(ref, {
      [found.bucket]: sub[found.bucket].filter(it => it.itemId !== itemId),
      updatedAt: serverTimestamp(),
    });
  });
}

function locateItem(
  sub: EPRSubmission,
  itemId: string,
): { bucket: ItemBucket; index: number; item: EPRItem } | null {
  const buckets: ItemBucket[] = ['okrItems', 'kpiItems', 'coreItems', 'leadershipItems'];
  for (const bucket of buckets) {
    const arr = sub[bucket];
    const idx = arr.findIndex(it => it.itemId === itemId);
    if (idx >= 0) return { bucket, index: idx, item: arr[idx] };
  }
  return null;
}

// ─── State transitions ─────────────────────────────────────────────────────

export async function submitByEmployee(
  id: string,
  employeeId: string,
): Promise<EPRSubmission> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new EPRSubmissionError('not_found', 'Submission not found.');
    const sub = snap.data() as EPRSubmission;

    if (sub.employeeId !== employeeId) {
      throw new EPRSubmissionError('unauthorized', 'Not your submission.');
    }
    if (sub.status !== 'draft') {
      throw new EPRSubmissionError(
        'wrong_state',
        `Cannot submit as employee from state '${sub.status}'.`,
      );
    }

    const issues = validateSubmissionForSubmit(sub, 'employee');
    if (issues.length > 0) {
      throw new EPRSubmissionError('validation_failed', 'Submission incomplete.', issues);
    }

    tx.update(ref, {
      status: 'employee_submitted' as EPRSubmissionStatus,
      submittedByEmployeeAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { ...sub, id, status: 'employee_submitted' };
  });
}

export async function submitByManager(
  id: string,
  managerId: string,
): Promise<EPRSubmission> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new EPRSubmissionError('not_found', 'Submission not found.');
    const sub = snap.data() as EPRSubmission;

    if (sub.supervisorId !== managerId) {
      throw new EPRSubmissionError('unauthorized', 'Not the assigned supervisor.');
    }
    if (sub.status !== 'employee_submitted') {
      throw new EPRSubmissionError(
        'wrong_state',
        `Cannot submit as manager from state '${sub.status}'.`,
      );
    }

    const issues = validateSubmissionForSubmit(sub, 'manager');
    if (issues.length > 0) {
      throw new EPRSubmissionError('validation_failed', 'Manager submission incomplete.', issues);
    }

    // Recompute final scores from the now-complete item data.
    // computeItemFinalScore may return `undefined` for items with no
    // user-entered score — guard against assigning undefined into the
    // doc (Firestore rejects it). Skip the field instead of writing
    // `itemFinalScore: undefined`.
    const recomputedItems = (bucket: ItemBucket) =>
      sub[bucket].map(it => {
        const f = computeItemFinalScore(it);
        return f !== undefined ? { ...it, itemFinalScore: f } : { ...it };
      });
    const okrItems        = recomputedItems('okrItems');
    const kpiItems        = recomputedItems('kpiItems');
    const coreItems       = recomputedItems('coreItems');
    const leadershipItems = recomputedItems('leadershipItems');

    const scores = computeSubmissionScores({
      ...sub,
      okrItems, kpiItems, coreItems, leadershipItems,
    });

    // Deep-prune undefined leaves before writing. Items may carry
    // optional fields (notesEvidence, target, actual, achievementPct,
    // employeeSelfScore, managerScore) that legitimately can be unset;
    // scores object may also have undefined sub-keys when a section
    // wasn't filled. Without omitUndefinedDeep, Firestore aborts the
    // whole tx with "Unsupported field value: undefined".
    const payload = omitUndefinedDeep({
      okrItems, kpiItems, coreItems, leadershipItems,
      scores,
      status: 'manager_submitted' as EPRSubmissionStatus,
      submittedByManagerAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    tx.update(ref, payload);
    return {
      ...sub, id,
      okrItems, kpiItems, coreItems, leadershipItems,
      scores,
      status: 'manager_submitted',
    };
  });
}

/**
 * Lock for calibration. HR-only in practice; the service does not enforce
 * the role (Firestore rules + callsite must), but it does refuse to lock
 * a submission not yet manager_submitted.
 *
 * One-way. Post-lock changes happen only via calibrationAdjustment docs
 * per §E.6.
 */
export async function lockSubmission(
  id: string,
  hrUserId: string,
): Promise<EPRSubmission> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new EPRSubmissionError('not_found', 'Submission not found.');
    const sub = snap.data() as EPRSubmission;

    if (sub.status !== 'manager_submitted') {
      throw new EPRSubmissionError(
        'wrong_state',
        `Cannot lock from state '${sub.status}'.`,
      );
    }

    tx.update(ref, {
      status: 'locked' as EPRSubmissionStatus,
      lockedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // hrUserId stored as updatedBy via separate audit if needed; left as
      // a Phase-1 omission. Firestore server logs capture the writer.
    });
    void hrUserId;
    return { ...sub, id, status: 'locked' };
  });
}
